import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  CircularProgress,
  FormHelperText,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

interface VideoLimit {
  id: string;
  sport: string;
  exam_type: string;
  exam_category: string | null;
  exam_item_id: string | null;
  max_file_size_mb: number;
  max_duration_seconds: number;
  min_duration_seconds: number;
  max_attempts: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface AdminVideoLimitsTabProps {
  sport: string;
}

const AdminVideoLimitsTab: React.FC<AdminVideoLimitsTabProps> = ({ sport }) => {
  const [limits, setLimits] = useState<VideoLimit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLimit, setEditingLimit] = useState<VideoLimit | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [limitToDelete, setLimitToDelete] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    sport: sport || 'Badminton',
    examType: 'stroke-shot',
    examCategory: '',
    examItemId: '',
    maxFileSizeMB: 250,
    maxDurationSeconds: 60,
    minDurationSeconds: 5,
    maxAttempts: 3,
    isActive: true,
  });

  const examTypes = ['stroke-shot', 'drill', 'fitness'];
  const sports = ['Badminton', 'Cricket', 'Tennis', 'Football', 'Athletics'];

  useEffect(() => {
    loadLimits();
  }, [sport]);

  const loadLimits = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(`/api/practical-exam/video-limits/all?sport=${sport}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      if (data.success) {
        setLimits(data.data || []);
      } else {
        setError(data.message || 'Failed to load video limits');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load video limits');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (limit?: VideoLimit) => {
    if (limit) {
      setEditingLimit(limit);
      setFormData({
        sport: limit.sport,
        examType: limit.exam_type,
        examCategory: limit.exam_category || '',
        examItemId: limit.exam_item_id || '',
        maxFileSizeMB: limit.max_file_size_mb,
        maxDurationSeconds: limit.max_duration_seconds,
        minDurationSeconds: limit.min_duration_seconds || 5,
        maxAttempts: limit.max_attempts || 3,
        isActive: limit.is_active,
      });
    } else {
      setEditingLimit(null);
      setFormData({
        sport: sport || 'Badminton',
        examType: 'stroke-shot',
        examCategory: '',
        examItemId: '',
        maxFileSizeMB: 250,
        maxDurationSeconds: 60,
        minDurationSeconds: 5,
        maxAttempts: 3,
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingLimit(null);
  };

  const handleSave = async () => {
    if (!formData.sport || !formData.examType || !formData.maxFileSizeMB || !formData.maxDurationSeconds) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const payload = {
        ...(editingLimit && { id: editingLimit.id }),
        sport: formData.sport,
        examType: formData.examType,
        examCategory: formData.examCategory || null,
        examItemId: formData.examItemId || null,
        maxFileSizeMB: formData.maxFileSizeMB,
        maxDurationSeconds: formData.maxDurationSeconds,
        minDurationSeconds: formData.minDurationSeconds,
        maxAttempts: formData.maxAttempts,
        isActive: formData.isActive,
      };

      const response = await fetch('/api/practical-exam/video-limits', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(editingLimit ? 'Video limit updated successfully' : 'Video limit created successfully');
        handleCloseDialog();
        loadLimits();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to save video limit');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save video limit');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id: string) => {
    setLimitToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!limitToDelete) return;

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const response = await fetch(`/api/practical-exam/video-limits/${limitToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setSuccess('Video limit deleted successfully');
        setDeleteConfirmOpen(false);
        setLimitToDelete(null);
        loadLimits();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.message || 'Failed to delete video limit');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete video limit');
    } finally {
      setLoading(false);
    }
  };

  const getRuleTypeLabel = (limit: VideoLimit) => {
    if (limit.exam_item_id) return 'Item Specific';
    if (limit.exam_category) return 'Category Specific';
    return 'Type Specific';
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Video Upload Limits - {sport}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadLimits}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Limit
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading && limits.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : limits.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No video limits configured for {sport}. Click "Add Limit" to create one.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Sport</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Exam Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Rule Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Max Size (MB)</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Max Duration (s)</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Min Duration (s)</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Max Attempts</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {limits.map((limit) => (
                    <TableRow key={limit.id} hover>
                      <TableCell>{limit.sport}</TableCell>
                      <TableCell>
                        <Chip label={limit.exam_type} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>{limit.exam_category || '-'}</TableCell>
                      <TableCell>
                        <Chip label={getRuleTypeLabel(limit)} size="small" />
                      </TableCell>
                      <TableCell>{limit.max_file_size_mb}</TableCell>
                      <TableCell>{limit.max_duration_seconds}</TableCell>
                      <TableCell>{limit.min_duration_seconds || 5}</TableCell>
                      <TableCell>{limit.max_attempts || 3}</TableCell>
                      <TableCell>
                        <Chip
                          label={limit.is_active ? 'Active' : 'Inactive'}
                          color={limit.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenDialog(limit)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(limit.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLimit ? 'Edit Video Limit' : 'Add Video Limit'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Sport</InputLabel>
              <Select
                value={formData.sport}
                label="Sport"
                onChange={(e) => setFormData({ ...formData, sport: e.target.value })}
              >
                {sports.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Exam Type</InputLabel>
              <Select
                value={formData.examType}
                label="Exam Type"
                onChange={(e) => setFormData({ ...formData, examType: e.target.value })}
              >
                {examTypes.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Category (Optional)"
              value={formData.examCategory}
              onChange={(e) => setFormData({ ...formData, examCategory: e.target.value })}
              helperText="Leave empty for type-specific rule"
            />

            <TextField
              fullWidth
              label="Exam Item ID (Optional)"
              value={formData.examItemId}
              onChange={(e) => setFormData({ ...formData, examItemId: e.target.value })}
              helperText="Leave empty for category/type-specific rule"
            />

            <TextField
              fullWidth
              required
              type="number"
              label="Max File Size (MB)"
              value={formData.maxFileSizeMB}
              onChange={(e) => setFormData({ ...formData, maxFileSizeMB: parseInt(e.target.value) || 0 })}
              inputProps={{ min: 1 }}
            />

            <TextField
              fullWidth
              required
              type="number"
              label="Max Duration (seconds)"
              value={formData.maxDurationSeconds}
              onChange={(e) => setFormData({ ...formData, maxDurationSeconds: parseInt(e.target.value) || 0 })}
              inputProps={{ min: 1 }}
            />

            <TextField
              fullWidth
              type="number"
              label="Min Duration (seconds)"
              value={formData.minDurationSeconds}
              onChange={(e) => setFormData({ ...formData, minDurationSeconds: parseInt(e.target.value) || 0 })}
              inputProps={{ min: 0 }}
              helperText="Default: 5 seconds"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : editingLimit ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Video Limit</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this video limit? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminVideoLimitsTab;

